/* eslint-disable @next/next/no-img-element */
import { useCallback, useEffect, useRef } from 'react';
import { CategoryMessageType, MediaMachineCategory } from './MediaMachine';
import styles from './MediaMachineCategory.module.sass';
import Icon from '@mdi/react';
import {
    mdiArrowDown,
    mdiArrowUp,
    mdiCamera,
    mdiCameraPlus,
    mdiCameraRetake,
    mdiDelete,
    mdiFormTextbox,
    mdiMenu,
} from '@mdi/js';
import EmojiText from '@/components/EmojiText/EmojiText';
import SoftPopup, {
    SoftPopupButton,
    SoftPopupDivider,
} from '@/components/SoftPopup/SoftPopup';
import { measure } from '@/components/Analytics/Analytics';

type MediaMachineCategoryProps = {
    frameRef: React.RefObject<HTMLIFrameElement | null>;
    isEmbedded?: boolean;
    isFirstCategoryWithNotEnoughSamples: boolean;
    category: MediaMachineCategory;
    setCategoryName: (name: string) => void;
    setCategorySamples: (samples: number) => void;
    thumbnails: string[];
    setThumbnails: (thumbnails: string[]) => void;
    possibleMoves: Set<-1 | 1>;
    moveBy: (by: -1 | 1) => void;
    sendMessage: (
        type: CategoryMessageType,
        data: Record<string, unknown>,
    ) => void;
    canBeRemoved: boolean;
    removeCategory: () => void;
};

export const MediaMachineCameraCategory = ({
    frameRef,
    isEmbedded,
    isFirstCategoryWithNotEnoughSamples,
    category,
    setCategoryName,
    setCategorySamples,
    thumbnails,
    setThumbnails,
    sendMessage,
    possibleMoves,
    moveBy,
    canBeRemoved,
    removeCategory,
}: MediaMachineCategoryProps) => {
    const nameInputRef = useRef<HTMLInputElement>(null);

    const onMachineFrameMessage = useCallback((event: MessageEvent) => {
        if (event.data?._hetklokhuisaistudio) {
            switch (event.data.type) {
                case 'category.samplecount':
                    measure(
                        `media_machine_camera_category_recorded_samples_${
                            event.data.sampleCount[category.id] ?? 0
                        }`,
                    );
                    setCategorySamples(
                        event.data.sampleCount[category.id] ?? 0,
                    );
                    break;
                case 'category.thumbnails':
                    if (event.data.classId === category.id) {
                        setThumbnails(event.data.thumbnails);
                    }
                    break;
            }
        }
    }, []);

    useEffect(() => {
        if (frameRef.current) {
            window.addEventListener('message', onMachineFrameMessage);
        }

        return () => {
            window.removeEventListener('message', onMachineFrameMessage);
        };
    }, [frameRef.current]);

    const categoryName =
        category.name.length > 0 ? category.name : 'naamloze categorie';

    return (
        <li
            className={`${styles['mediamachinecameracategory']} ${
                isEmbedded
                    ? `${styles['mediamachinecameracategory--embedded']} bg-${category.color} hover:ring-${category.color}`
                    : styles['mediamachinecameracategory--full']
            }`}
        >
            <div className={styles['mediamachinecameracategory__info']}>
                <div
                    className={styles['mediamachinecameracategory__info__row']}
                >
                    <SoftPopup
                        className={
                            styles[
                                'mediamachinecameracategory__info__row__options'
                            ]
                        }
                        buttonProps={{
                            className:
                                styles[
                                    'mediamachinecameracategory__info__row__options__button'
                                ],
                            'aria-label': `Opties voor '${categoryName}'`,
                            title: `Opties voor '${categoryName}'`,
                            children: <Icon path={mdiMenu} size={1.1} />,
                        }}
                    >
                        {({ close }) => (
                            <>
                                <SoftPopupButton
                                    icon={mdiFormTextbox}
                                    onClick={() => {
                                        close();
                                        nameInputRef.current?.focus();
                                    }}
                                >
                                    Naam veranderen
                                </SoftPopupButton>
                                {possibleMoves.has(-1) && (
                                    <SoftPopupButton
                                        icon={mdiArrowUp}
                                        onClick={() => {
                                            close();
                                            moveBy(-1);
                                        }}
                                    >
                                        Naar boven
                                    </SoftPopupButton>
                                )}
                                {possibleMoves.has(1) && (
                                    <SoftPopupButton
                                        icon={mdiArrowDown}
                                        onClick={() => {
                                            close();
                                            moveBy(1);
                                        }}
                                    >
                                        Naar beneden
                                    </SoftPopupButton>
                                )}
                                {(category.samples > 0 || canBeRemoved) && (
                                    <SoftPopupDivider />
                                )}
                                {category.samples > 0 && (
                                    <SoftPopupButton
                                        icon={mdiCameraRetake}
                                        onClick={() => {
                                            close();
                                            sendMessage('category.clear', {
                                                classInfo: category,
                                            });
                                        }}
                                    >
                                        Foto&apos;s wissen
                                    </SoftPopupButton>
                                )}
                                {canBeRemoved && (
                                    <SoftPopupButton
                                        icon={mdiDelete}
                                        onClick={() => {
                                            close();
                                            sendMessage('category.clear', {
                                                classInfo: category,
                                            });
                                            removeCategory();
                                        }}
                                    >
                                        Categorie verwijderen
                                    </SoftPopupButton>
                                )}
                            </>
                        )}
                    </SoftPopup>
                    <input
                        ref={nameInputRef}
                        type="text"
                        maxLength={32}
                        value={category.name}
                        onChange={(event) =>
                            setCategoryName(event.target.value)
                        }
                        placeholder="Naam geven"
                        aria-label="Naam van de categorie"
                        className={`${styles['mediamachinecameracategory__info__row__nameinput']} ring-${category.color} focus:ring-${category.color} bg-${category.color}`}
                    />
                </div>
                <button
                    className={`${
                        styles['mediamachinecameracategory__info__recordbutton']
                    } ${
                        isEmbedded
                            ? `text-${category.color}`
                            : `bg-${category.color}`
                    } ${
                        isFirstCategoryWithNotEnoughSamples
                            ? styles[
                                  'mediamachinecameracategory__info__recordbutton--attention'
                              ]
                            : ''
                    }`}
                    onClick={() => {
                        measure('media_machine_camera_category_button_record');
                        sendMessage('category.record', {
                            classInfo: category,
                        });
                    }}
                    aria-label={`Foto toevoegen aan '${categoryName}'`}
                    title={`Foto toevoegen aan '${categoryName}'`}
                >
                    <Icon
                        path={isEmbedded ? mdiCameraPlus : mdiCamera}
                        size={1}
                    />
                    {isEmbedded ? (
                        <EmojiText>{category.name}</EmojiText>
                    ) : (
                        'Toevoegen'
                    )}
                </button>
            </div>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
                className={`${styles['mediamachinecameracategory__thumbnails']} bg-${category.color}`}
                onClick={() => {
                    sendMessage('category.record', {
                        classInfo: category,
                    });
                }}
            >
                {thumbnails.map((thumbnail, index) => (
                    <img
                        key={index}
                        src={thumbnail}
                        alt=""
                        className="w-full"
                        aria-hidden
                    />
                ))}
                <div
                    className={`${styles['mediamachinecameracategory__thumbnails__helptext']}`}
                >
                    {category.samples < 4
                        ? `Maak 5 of meer foto's`
                        : `${category.samples} foto's`}
                </div>
            </div>
        </li>
    );
};
